import { useCallback, useEffect, useState } from "react";
import { Modal } from "antd";
import { LuBrainCircuit } from "react-icons/lu";
import { HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";
import PageContainer from "../../../components/page-container";
import Button from "../../../components/button/button";
import Message from "../../../components/toast-message";
import { buildAgentNavigation } from "../../../consts/agent-nav-config";
import { useAgentConfigSource } from "../../../helpers/hooks/agent-config-source";
import {
	SCOPE_USER,
	getMemoryConfig,
	saveMemoryConfig,
	getMemoryEntries,
	createMemoryEntry,
	updateMemoryEntry,
	deleteMemoryEntry,
	clearMemoryScope,
} from "../../../services/memory";

const emptyMemoryData = () => ({
	scope: SCOPE_USER,
	entries: [],
	usedChars: 0,
	capChars: 0,
	usagePct: 0,
});

// ─── UsageBar ─────────────────────────────────────────────────────────────────
// Memory is a fixed budget, not a growing log: every entry competes for the same
// space in the system prompt. Showing the bar is what makes that tradeoff legible
// before a save is rejected for exceeding the cap.
function UsageBar({ usedChars, capChars, usagePct }) {
	if (!capChars) return null;
	const pct = Math.min(usagePct || 0, 100);
	const tone =
		pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-primary";

	return (
		<div className="mt-3">
			<div className="flex items-center justify-between text-xs text-gray-500 mb-1">
				<span>
					{usedChars} / {capChars} characters
				</span>
				<span>{pct}%</span>
			</div>
			<div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden">
				<div className={`h-full ${tone} transition-all`} style={{ width: `${pct}%` }} />
			</div>
			{pct >= 80 && (
				<p className="text-xs text-amber-600 mt-1">
					Near capacity — the agent will be asked to consolidate before it can store
					anything new.
				</p>
			)}
		</div>
	);
}

// ─── EntryRow ─────────────────────────────────────────────────────────────────
function EntryRow({ entry, onEdit, onDelete }) {
	return (
		<div className="flex flex-row items-start justify-between border border-gray-200 rounded-lg bg-white px-4 py-3 hover:shadow-sm transition-shadow">
			<div className="flex-1 min-w-0 pr-4">
				<p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
					{entry.text}
				</p>
				<div className="flex items-center gap-2 mt-1">
					<span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
						{entry.source === "agent" ? "Learned by agent" : "Added by you"}
					</span>
					<span className="text-xs text-gray-400">
						{(entry.text || "").length} chars
					</span>
				</div>
			</div>
			<div className="flex items-center gap-2 flex-shrink-0">
				<button
					type="button"
					onClick={() => onEdit(entry)}
					className="p-1.5 text-gray-500 hover:text-primary transition-colors rounded"
					title="Edit entry"
				>
					<HiOutlinePencil className="h-4 w-4" />
				</button>
				<button
					type="button"
					onClick={() => onDelete(entry)}
					className="p-1.5 text-gray-500 hover:text-red-500 transition-colors rounded"
					title="Delete entry"
				>
					<HiOutlineTrash className="h-4 w-4" />
				</button>
			</div>
		</div>
	);
}

// ─── EntryModal ───────────────────────────────────────────────────────────────
function EntryModal({ open, entry, appId, userId, onClose, onSaved }) {
	const isEdit = !!entry?.id;
	const [text, setText] = useState("");
	const [saveState, setSaveState] = useState({ processing: false, failed: false });
	const [error, setError] = useState("");

	useEffect(() => {
		if (open) {
			setText(entry?.text || "");
			setSaveState({ processing: false, failed: false });
			setError("");
		}
	}, [open, entry]);

	const handleSave = async () => {
		const value = text.trim();
		if (!value) {
			setError("Entry text is required.");
			return;
		}
		setError("");
		setSaveState({ processing: true, failed: false });

		// Memory is partitioned per end user, so every write carries the
		// userId — the backend cannot resolve a record without it.
		const payload = { text: value, source: "console", userId };

		const result = isEdit
			? await updateMemoryEntry(appId, SCOPE_USER, entry.id, payload)
			: await createMemoryEntry(appId, SCOPE_USER, payload);

		if (result?.status === 200 || result?.status === 201) {
			onSaved();
		} else {
			setSaveState({ processing: false, failed: true });
			// The cap and the injection scrub are enforced server-side, so the
			// message it returns is the useful one to show.
			setError(result?.response || "Failed to save entry. Please try again.");
		}
	};

	return (
		<Modal
			title={
				<span className="font-semibold text-gray-800">
					{isEdit ? "Edit Memory Entry" : "Add Memory Entry"}
				</span>
			}
			open={open}
			onCancel={onClose}
			width={640}
			destroyOnClose
			footer={
				<div className="flex justify-end gap-2">
					<Button text="Cancel" color="default" onClick={onClose} disabled={saveState.processing} />
					<Button
						text={isEdit ? "Update" : "Save"}
						onClick={handleSave}
						loading={saveState.processing}
					/>
				</div>
			}
		>
			<div className="flex flex-col gap-4 mt-4">
				{error && <Message text={error} type="failure" />}

				<div>
					<label className="block text-sm font-semibold text-gray-800 mb-1">
						Entry <span className="text-red-500">*</span>
					</label>
					<p className="text-xs text-gray-500 mb-1">
						One durable fact, written so it still makes sense on its own months from
						now. This text is added to the agent's prompt on every conversation.
					</p>
					<textarea
						value={text}
						onChange={(e) => setText(e.target.value)}
						rows={5}
						placeholder="Prefers to be contacted in Sinhala."
						className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
					/>
					<p className="text-xs text-gray-400 mt-1">{text.trim().length} characters</p>
				</div>
			</div>
		</Modal>
	);
}

// ─── MemoryPanel ──────────────────────────────────────────────────────────────
// One user's memory: usage, stored entries, edit / delete / clear.
function MemoryPanel({ appId, capChars, data, loading, onReload, userId }) {
	const [modalOpen, setModalOpen] = useState(false);
	const [editingEntry, setEditingEntry] = useState(null);
	const [deletingEntry, setDeletingEntry] = useState(null);
	const [clearing, setClearing] = useState(false);

	// Manual "Add entry" is hidden for now — memory is written by the agent.
	// Kept so it can be switched back on by restoring the button below.
	// const handleAdd = () => {
	// 	setEditingEntry(null);
	// 	setModalOpen(true);
	// };

	const handleEdit = (entry) => {
		setEditingEntry(entry);
		setModalOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (!deletingEntry) return;
		await deleteMemoryEntry(appId, SCOPE_USER, deletingEntry.id, userId);
		setDeletingEntry(null);
		onReload();
	};

	const handleClear = async () => {
		setClearing(true);
		await clearMemoryScope(appId, SCOPE_USER, userId);
		setClearing(false);
		onReload();
	};

	return (
		<div className="border border-gray-200 rounded-lg bg-white p-5">
			<div>
				<h3 className="text-sm font-semibold text-gray-800">User memory</h3>
				<p className="text-xs text-gray-500 mt-0.5">
					What the agent remembers about this user — what they said about themselves and
					what the agent worked out while helping them.
				</p>
			</div>

			<UsageBar
				usedChars={data.usedChars}
				capChars={data.capChars || capChars}
				usagePct={data.usagePct}
			/>

			{/* Entries */}
			<div className="mt-5">
				<div className="flex items-center justify-between mb-2">
					<p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
						Stored entries
					</p>
					<div className="flex items-center gap-2">
						{data.entries.length > 0 && (
							<button
								type="button"
								onClick={handleClear}
								disabled={clearing}
								className="text-xs text-gray-500 hover:text-red-500 disabled:opacity-40"
							>
								{clearing ? "Clearing…" : "Clear all"}
							</button>
						)}
						{/* Hidden for now — see handleAdd above. */}
						{/* <Button text="Add entry" onClick={handleAdd} /> */}
					</div>
				</div>

				{loading ? (
					<p className="text-xs text-gray-400 py-6 text-center">Loading entries…</p>
				) : data.entries.length === 0 ? (
					<p className="text-xs text-gray-400 py-6 text-center">
						Nothing stored yet. The agent adds entries as it learns about this user.
					</p>
				) : (
					<div className="flex flex-col gap-2">
						{data.entries.map((entry) => (
							<EntryRow
								key={entry.id}
								entry={entry}
								onEdit={handleEdit}
								onDelete={setDeletingEntry}
							/>
						))}
					</div>
				)}
			</div>

			<EntryModal
				open={modalOpen}
				entry={editingEntry}
				appId={appId}
				userId={userId}
				onClose={() => setModalOpen(false)}
				onSaved={() => {
					setModalOpen(false);
					onReload();
				}}
			/>

			<Modal
				title={<span className="font-semibold text-gray-800">Delete Entry</span>}
				open={!!deletingEntry}
				onCancel={() => setDeletingEntry(null)}
				footer={
					<div className="flex justify-end gap-2">
						<Button text="Cancel" color="default" onClick={() => setDeletingEntry(null)} />
						<Button text="Delete" color="error" onClick={handleDeleteConfirm} />
					</div>
				}
			>
				<p className="text-sm text-gray-600 mt-2">
					Delete this entry? The agent will stop seeing it on the next conversation.
				</p>
			</Modal>
		</div>
	);
}

// ─── Main Page ────────────────────────────────────────────────────────────────
// Memory is on for every app. It can only be switched off by hand in the
// database (`disabled: true` on the memory-config record), so there is no
// toggle here.
export default function AgentMemory() {
	const { appId, subAgentId, subAgent } = useAgentConfigSource();
	const navigation = buildAgentNavigation({ subAgentId });

	const [config, setConfig] = useState(null);
	const [loading, setLoading] = useState(true);
	const [saveError, setSaveError] = useState("");

	const [memoryData, setMemoryData] = useState(emptyMemoryData());
	const [entriesLoading, setEntriesLoading] = useState(false);

	// Memory is one store per end user, so the console has to be told which
	// user to look at. This is the same id the runtime reads from
	// requestAttributes.userId.
	const [userIdInput, setUserIdInput] = useState("");
	const [activeUserId, setActiveUserId] = useState("");

	const loadConfig = useCallback(async () => {
		if (!appId) return;
		setLoading(true);
		setConfig(await getMemoryConfig(appId));
		setLoading(false);
	}, [appId]);

	const loadEntries = useCallback(async () => {
		if (!appId) return;
		// Memory is partitioned per end user, so nothing can be read until a
		// user is chosen — there is no shared partition to fall back to.
		if (!activeUserId) {
			setMemoryData(emptyMemoryData());
			return;
		}
		setEntriesLoading(true);
		const data = await getMemoryEntries(appId, SCOPE_USER, activeUserId);
		setMemoryData(data || emptyMemoryData());
		setEntriesLoading(false);
	}, [appId, activeUserId]);

	useEffect(() => {
		loadConfig();
	}, [loadConfig]);

	useEffect(() => {
		loadEntries();
	}, [loadEntries]);

	const persistConfig = async (next) => {
		setSaveError("");
		const result = await saveMemoryConfig(appId, next);
		if (result?.status === 200 || result?.status === 201) {
			setConfig(result.response || next);
		} else {
			setSaveError(result?.response || "Failed to save memory settings.");
			// Re-read so the UI reflects what is actually stored, not the
			// optimistic value that failed.
			loadConfig();
		}
	};

	// Local edit only; saved on blur so a half-typed number is not validated.
	const setUserScopeConfig = (patch) => {
		setConfig({
			...config,
			scopes: {
				...(config.scopes || {}),
				[SCOPE_USER]: { ...((config.scopes || {})[SCOPE_USER] || {}), ...patch },
			},
		});
	};

	if (loading || !config) {
		return (
			<PageContainer
				title="Memory"
				subtitle="What the agent remembers across conversations"
				navigationPosition="below-header"
				navigation={navigation}
			>
				<div className="flex justify-center items-center py-20">
					<p className="text-sm text-gray-400">Loading memory settings…</p>
				</div>
			</PageContainer>
		);
	}

	const userScope = (config.scopes || {})[SCOPE_USER] || {};
	const inputClass =
		"w-32 border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary";

	return (
		<PageContainer
			title="Memory"
			subtitle={
				subAgentId
					? `What ${subAgent?.name || "this sub-agent"} remembers across conversations`
					: "What the agent remembers across conversations"
			}
			navigationPosition="below-header"
			navigation={navigation}
		>
			<div className="mt-6 px-1 flex flex-col gap-5">
				{saveError && <Message text={saveError} type="failure" />}

				{/* Settings — apply to every user */}
				<div className="border border-gray-200 rounded-lg bg-white p-5 flex flex-col gap-4">
					<div>
						<h3 className="text-sm font-semibold text-gray-800">Persistent memory</h3>
						<p className="text-xs text-gray-500 mt-0.5">
							The agent keeps notes about each user that survive between conversations.
							Stored memory is added to its prompt on every turn.
						</p>
					</div>

					{/* Memory instructions — hidden for now. A stored value is still applied by
					    the runtime; restore this block to make it editable again.
					<div>
						<label className="block text-sm font-semibold text-gray-800 mb-1">
							Memory instructions
						</label>
						<p className="text-xs text-gray-500 mb-1">
							Optional. Extra guidance on what is worth remembering, appended to the
							memory section of the prompt.
						</p>
						<textarea
							value={config.instructions || ""}
							onChange={(e) => setConfig({ ...config, instructions: e.target.value })}
							onBlur={() => persistConfig(config)}
							rows={3}
							placeholder="Remember delivery preferences and past complaints. Never store payment details."
							className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
						/>
					</div>
					*/}

					<div className="flex flex-wrap items-start gap-4">
						<div>
							<label className="flex items-center h-5 text-xs font-semibold text-gray-700 mb-1">
								Character limit
							</label>
							<input
								type="number"
								min={200}
								max={20000}
								value={userScope.capChars ?? ""}
								onChange={(e) => setUserScopeConfig({ capChars: Number(e.target.value) })}
								onBlur={() => persistConfig(config)}
								className={inputClass}
							/>
							<p className="text-xs text-gray-400 mt-1">Per user, 200 – 20000.</p>
						</div>
						<div>
							<label className="flex items-center h-5 text-xs font-semibold text-gray-700 mb-1">
								Retention (days)
							</label>
							<input
								type="number"
								min={0}
								max={3650}
								value={userScope.retentionDays ?? 0}
								onChange={(e) => setUserScopeConfig({ retentionDays: Number(e.target.value) })}
								onBlur={() => persistConfig(config)}
								className={inputClass}
							/>
							<p className="text-xs text-gray-400 mt-1">0 keeps entries indefinitely.</p>
						</div>
					</div>
				</div>

				{/* Memory is partitioned per end user, so the entries surface is scoped
				    to one person. There is no "list all users" query — the key scheme
				    partitions by user, so browsing everyone would be a table scan —
				    hence a lookup box rather than a picker. */}
				<div className="border border-gray-200 rounded-lg bg-white p-5">
					<label className="block text-sm font-semibold text-gray-800 mb-1">
						Inspect Memory
					</label>
					<p className="text-xs text-gray-500 mb-2">
						Memory is stored per end user, keyed on the <span className="font-mono">userId</span>{" "}
						the channel sends in <span className="font-mono">requestAttributes</span>. Enter one to
						view or edit what the agent remembers about that person.
					</p>
					<div className="flex gap-2 max-w-md">
						<input
							type="text"
							value={userIdInput}
							onChange={(e) => setUserIdInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") setActiveUserId(userIdInput.trim());
							}}
							placeholder="userId"
							className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
						/>
						<Button text="Load" onClick={() => setActiveUserId(userIdInput.trim())} />
					</div>
					{activeUserId && (
						<p className="text-xs text-gray-500 mt-2">
							Showing memory for <span className="font-mono font-semibold">{activeUserId}</span>
						</p>
					)}
				</div>

				{activeUserId ? (
					<MemoryPanel
						appId={appId}
						capChars={userScope.capChars}
						data={memoryData}
						loading={entriesLoading}
						onReload={loadEntries}
						userId={activeUserId}
					/>
				) : (
					<div className="flex flex-col items-center justify-center py-12 text-center border border-dashed border-gray-200 rounded-lg">
						<div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 text-gray-400 mb-3">
							<LuBrainCircuit size={24} />
						</div>
						<p className="text-sm font-medium text-gray-600">
							Enter a user ID to inspect their memory
						</p>
						<p className="text-xs text-gray-400 mt-1 max-w-sm">
							Memory is stored per end user, so there is nothing to show until you choose
							one. The settings above apply to every user.
						</p>
					</div>
				)}
			</div>
		</PageContainer>
	);
}
